import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ShieldCheck, Terminal } from "lucide-react";
import usePageview from "@/lib/usePageview";

/**
 * Phase 25.13h — Privacy Policy.
 *
 * AU-compliant baseline drafted by the founder + GPT-grade legal-aligned
 * boilerplate. Designed to satisfy the prerequisites for:
 *   · Square Production app review
 *   · Stripe live payments
 *   · GDPR / Australian Privacy Act 1988 disclosure
 *   · Investor due diligence
 *
 * Replace `EFFECTIVE_DATE` and the contact email if your trading entity changes.
 * For high-risk customers, a lawyer should review before relying on this in
 * regulated industries (banking, healthcare, government tenders).
 */

const EFFECTIVE_DATE = "1 May 2026";
const ENTITY = "Dara Technology Pty Ltd (ACN to be confirmed)";
const CONTACT_EMAIL = "privacy@daraos.ai";

const SECTIONS = [
  {
    id: "01",
    title: "Who we are",
    body: (
      <>
        DARA OS (&quot;<strong>DARA</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;
        <strong>us</strong>&quot;) is a SaaS platform operated by {ENTITY},
        an Australian-registered company headquartered in Sydney, NSW. We provide
        a Bloomberg-style operating terminal that unifies a customer&apos;s POS,
        accounting, and treasury data sources into an AI-grounded daily briefing.
      </>
    ),
  },
  {
    id: "02",
    title: "Scope of this policy",
    body: (
      <>
        This Privacy Policy explains how DARA collects, uses, stores, and discloses
        information when you (i) visit{" "}
        <a href="https://daraos.ai" className="text-[#FFB000] underline">daraos.ai</a>,{" "}
        (ii) sign up for a DARA workspace, (iii) connect third-party services to
        your workspace, or (iv) communicate with us by email, support form, or any
        other channel. It applies to both the public website and the authenticated
        application.
      </>
    ),
  },
  {
    id: "03",
    title: "Information we collect",
    body: (
      <>
        <p className="mb-4">We collect the following categories of information:</p>
        <ul className="space-y-3 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li><strong>Account data:</strong> name, business name, role, email
            address, hashed password, industry classification, two-factor
            authentication seed (if enabled), and email-consent timestamp.</li>
          <li><strong>Workspace data:</strong> business data you upload, paste,
            or sync into your workspace — including (where you choose to connect
            them) transactions from your POS provider (e.g. Square), invoices
            from your accounting provider (e.g. Xero or MYOB), and structured
            CSV files you upload. This data belongs to you.</li>
          <li><strong>Operational logs:</strong> IP addresses, browser
            user-agents, request paths, and event timestamps, kept for security
            and abuse-prevention purposes for up to 90 days.</li>
          <li><strong>Billing data:</strong> payment-method metadata (card brand,
            last four digits, expiry) handled through Stripe — DARA does not
            store full card numbers.</li>
          <li><strong>Marketing data:</strong> if you tick our email consent
            box, we record the consent action with timestamp, IP, and source URL.</li>
        </ul>
      </>
    ),
  },
  {
    id: "04",
    title: "How we use your information",
    body: (
      <>
        <p className="mb-4">We use the data described above to:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>Provide and operate the DARA platform — including authentication,
            data ingestion, AI-grounded briefings, and integration with any
            third-party services you elect to connect.</li>
          <li>Bill you for subscriptions and process refunds through Stripe.</li>
          <li>Send transactional emails (account verification, billing receipts,
            security alerts) that are required to operate the service.</li>
          <li>Send marketing or product-update emails only where you have given
            explicit consent, with a one-click unsubscribe in every email.</li>
          <li>Monitor for fraud, abuse, performance issues, and unauthorised access.</li>
          <li>Comply with our legal obligations under Australian and other
            applicable law.</li>
        </ul>
      </>
    ),
  },
  {
    id: "05",
    title: "AI and your data",
    body: (
      <>
        <p className="mb-4">
          DARA uses Anthropic&apos;s Claude (Sonnet 4.5) large language model to
          synthesise your workspace data into daily briefings and answer your
          natural-language queries. We submit your data to the Claude API solely
          for the purpose of returning the response you have requested.
        </p>
        <p className="mb-4">
          Anthropic does not train its commercial models on data submitted via
          their API. We do not authorise or instruct any model provider to train
          on your data. We retain Claude prompts and responses associated with
          your workspace for up to 30 days for debugging and quality-assurance
          purposes, after which they are deleted.
        </p>
        <p>
          If you operate in a sector with strict data-residency requirements
          (defence, government, regulated finance) please contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFB000] underline">{CONTACT_EMAIL}</a>{" "}
          to discuss an air-gapped Enterprise deployment.
        </p>
      </>
    ),
  },
  {
    id: "06",
    title: "Third-party services we use",
    body: (
      <>
        <p className="mb-4">We rely on the following third-party services to operate DARA. Each provider operates its own privacy policy, which you should review:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li><strong>Anthropic</strong> — AI synthesis layer (Claude 4.5 Sonnet)</li>
          <li><strong>Stripe</strong> — payment processing</li>
          <li><strong>Resend</strong> — transactional and consent-based marketing email delivery</li>
          <li><strong>MongoDB Atlas</strong> — primary data store</li>
          <li><strong>Square, Xero, MYOB</strong> — only where you have explicitly connected one of these services to your workspace</li>
        </ul>
        <p className="mt-4">
          We do not sell, rent, or otherwise share your personal information with
          third parties for their own marketing purposes. We only share the minimum
          information necessary for these providers to deliver the services we
          contract them to perform.
        </p>
      </>
    ),
  },
  {
    id: "07",
    title: "Data security",
    body: (
      <>
        <p className="mb-4">We maintain commercially reasonable safeguards to protect your data, including:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>TLS 1.3 for all data in transit</li>
          <li>Fernet-encrypted vault for stored API keys and OAuth tokens</li>
          <li>Per-user TOTP two-factor authentication available on every account</li>
          <li>Server-side rate-limiting and brute-force protection on authentication endpoints</li>
          <li>Bcrypt password hashing</li>
          <li>Email-consent audit trail with IP + timestamp</li>
          <li>Audit log of administrative actions</li>
          <li>SOC 2 Type I attestation in progress (target 2026)</li>
        </ul>
      </>
    ),
  },
  {
    id: "08",
    title: "Your rights",
    body: (
      <>
        <p className="mb-4">If you reside in Australia, under the Privacy Act 1988 (Cth) you have the right to:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li>Access the personal information we hold about you</li>
          <li>Correct any personal information that is inaccurate or out of date</li>
          <li>Request that we delete your account and associated data (data deletion is also available self-serve within the app: Settings → Account → Delete account)</li>
          <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at{" "}
            <a href="https://oaic.gov.au" className="text-[#FFB000] underline" target="_blank" rel="noopener noreferrer">oaic.gov.au</a></li>
        </ul>
        <p className="mt-4">
          If you reside in the European Union or United Kingdom, the rights granted under the General Data Protection Regulation (GDPR) apply to you in addition to the above — including the right to data portability and the right to object to processing.
        </p>
      </>
    ),
  },
  {
    id: "09",
    title: "International data transfers",
    body: (
      <>
        DARA is hosted on infrastructure located primarily in Australia and the
        United States. Where data is transferred outside Australia, we rely on
        the privacy frameworks operated by our third-party providers (including
        EU Standard Contractual Clauses) and only transfer the minimum data
        necessary to deliver the service you requested.
      </>
    ),
  },
  {
    id: "10",
    title: "Data retention",
    body: (
      <>
        <p className="mb-4">We retain your data as follows:</p>
        <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed list-disc pl-5">
          <li><strong>Account data:</strong> for the lifetime of your account, plus 90 days after deletion to handle billing reconciliation and abuse investigations.</li>
          <li><strong>Workspace data:</strong> for the lifetime of your subscription, plus 30 days after subscription cancellation to allow for restoration if you reactivate.</li>
          <li><strong>Operational logs:</strong> up to 90 days.</li>
          <li><strong>Billing records:</strong> 7 years, as required by Australian tax law.</li>
        </ul>
      </>
    ),
  },
  {
    id: "11",
    title: "Children",
    body: (
      <>DARA is a B2B platform intended for use by businesses. We do not knowingly collect personal information from anyone under 18 years of age.</>
    ),
  },
  {
    id: "12",
    title: "Updates to this policy",
    body: (
      <>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. Material changes will be notified to active customers by email with at least 14 days&apos; advance notice. The most current version is always available at this URL.</>
    ),
  },
  {
    id: "13",
    title: "Contact us",
    body: (
      <>
        Privacy enquiries should be directed to{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFB000] underline">{CONTACT_EMAIL}</a>.
        Postal mail can be addressed to: {ENTITY}, c/o the Privacy Officer, Sydney NSW, Australia.
      </>
    ),
  },
];

export default function Privacy() {
  usePageview("/privacy");
  return (
    <div className="min-h-screen bg-black text-white">
      {/* nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-black tracking-tight" data-testid="privacy-home-link">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            DARA OS
          </Link>
          <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> Back home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" /> LEGAL · PRIVACY POLICY
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-3">
          Privacy Policy
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-12">
          Effective: {EFFECTIVE_DATE} · Operated by {ENTITY}
        </p>

        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`privacy-section-${s.id}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// {s.id}</div>
              <h2 className="font-heading font-black text-2xl tracking-tight mb-4">{s.title}</h2>
              <div className="text-zinc-300 text-base leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-900 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          {ENTITY} · {EFFECTIVE_DATE} · v1.0
        </div>
      </main>
    </div>
  );
}
